export interface StacExistingAssetRow {
  id: string;
  title: string;
  description: string;
  type: string;
  roles: string[];
  size: number | null;
  href: string;
}

function readAssetSize(asset: Record<string, unknown>): number | null {
  const direct = asset['size'];
  if (typeof direct === 'number' && Number.isFinite(direct)) {
    return direct;
  }
  const fileSize = asset['file:size'];
  if (typeof fileSize === 'number' && Number.isFinite(fileSize)) {
    return fileSize;
  }
  const props = asset['properties'];
  if (props && typeof props === 'object') {
    const nested = (props as Record<string, unknown>)['file:size'];
    if (typeof nested === 'number' && Number.isFinite(nested)) {
      return nested;
    }
  }
  return null;
}

function readAssetRoles(asset: Record<string, unknown>): string[] {
  const roles = asset['roles'];
  if (!Array.isArray(roles)) {
    return [];
  }
  return roles.map((role) => String(role ?? '').trim()).filter(Boolean);
}

/** Normalize STAC item assets map into table rows. */
export function parseStacItemAssets(
  assets: Record<string, unknown> | undefined,
): StacExistingAssetRow[] {
  if (!assets || typeof assets !== 'object') {
    return [];
  }

  return Object.entries(assets)
    .map(([id, raw]) => {
      if (!raw || typeof raw !== 'object') {
        return null;
      }
      const asset = raw as Record<string, unknown>;
      const href = String(asset['href'] ?? '').trim();
      if (!href) {
        return null;
      }
      return {
        id,
        title: String(asset['title'] ?? id).trim() || id,
        description: String(asset['description'] ?? '').trim(),
        type: String(asset['type'] ?? '').trim(),
        roles: readAssetRoles(asset),
        size: readAssetSize(asset),
        href,
      };
    })
    .filter((row): row is StacExistingAssetRow => row !== null)
    .sort((left, right) => left.title.localeCompare(right.title));
}

/** Human-readable file size for asset table display. */
export function formatAssetByteSize(size: number | null): string {
  if (size === null || !Number.isFinite(size) || size < 0) {
    return '—';
  }
  if (size < 1024) {
    return `${size} B`;
  }
  if (size < 1024 * 1024) {
    return `${(size / 1024).toFixed(1)} KB`;
  }
  if (size < 1024 * 1024 * 1024) {
    return `${(size / (1024 * 1024)).toFixed(1)} MB`;
  }
  return `${(size / (1024 * 1024 * 1024)).toFixed(1)} GB`;
}

/** Sanitize a filename for browser download. */
export function sanitizeDownloadFilename(name: string, fallback: string): string {
  const trimmed = name.trim().replace(/[<>:"/\\|?*\x00-\x1F]/g, '_');
  return trimmed || fallback;
}
